import Gigya, { DataCenter } from './gigya.js'
import RBA from './rba.js'
import Webhooks from './accounts.webhooks.js'
import Account from './interfaces/account.js'
import SessionInfo from './interfaces/session-info.js'
import TargetEnv from './interfaces/target-env.js'
import SessionExpiration from './interfaces/session-expiration.js'
import Counter from './interfaces/counter.js'
import Profile from './interfaces/profile.js'
import BaseParams from './interfaces/base-params.js'
import Groups from './accounts.groups.js'
import Identity from './interfaces/identity.js'
import { Preferences } from './interfaces/preferences.js'
import Subscriptions from './interfaces/subscriptions.js'
import { B2B } from './accounts.b2b.js'
import { Communication } from './accounts.communication.js'
import { Communications } from './interfaces/communications.js'
import { Auth } from './accounts.auth.js'

export * from './rba.js'
export * from './accounts.webhooks.js'
export * from './interfaces/account.js'
export * from './interfaces/session-info.js'
export * from './interfaces/gigya-response.js'
export * from './interfaces/target-env.js'
export * from './interfaces/session-expiration.js'
export * from './interfaces/counter.js'
export * from './interfaces/profile.js'
export * from './interfaces/base-params.js'

export class Accounts {
	public readonly auth: Auth
	public readonly communication: Communication
	public readonly groups: Groups
	public readonly rba: RBA
	public readonly webhooks: Webhooks
	public readonly b2b: B2B

	constructor(protected gigya: Gigya) {
		this.auth = new Auth(gigya)
		this.communication = new Communication(gigya)
		this.groups = new Groups(gigya)
		this.rba = new RBA(gigya)
		this.webhooks = new Webhooks(gigya)
		this.b2b = new B2B(gigya)
	}

	/**
	 * This method deletes the specified user's account from Gigya's database.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.deleteAccount+REST
	 */
	public deleteAccount(params: BaseParams & AccountsDeleteAccountParams) {
		return this.gigya.request('accounts.deleteAccount', params)
	}

	/**
	 * This method deletes a screen-set hosted by Gigya.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.deleteScreenSet+REST
	 */
	public deleteScreenSet(params: BaseParams & AccountsDeleteScreenSetParams) {
		return this.gigya.request('accounts.deleteScreenSet', params)
	}

	/**
	 * This method allows sites integrating 3rd party plugins to validate the UID of a logged-in user.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.exchangeUIDSignature+REST
	 */
	public exchangeUIDSignature(params: BaseParams & AccountsExchangeUIDSignatureParams) {
		return this.gigya.request<AccountsExchangeUIDSignatureResponse>(
			'accounts.exchangeUIDSignature',
			params
		)
	}

	/**
	 * This method completes on-site user registration.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.finalizeRegistration+REST
	 */
	public finalizeRegistration(params: BaseParams & AccountsFinalizeRegistrationParams) {
		return this.gigya.request<Account & SessionInfo>('accounts.finalizeRegistration', params)
	}

	/**
	 * This method retrieves user account data.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getAccountInfo+REST
	 */
	public getAccountInfo(params: BaseParams & AccountsGetAccountInfoParams) {
		const fields = ['include', 'extraProfileFields', 'includeCommunications'] as const
		type Field = (typeof fields)[number]
		const overrides: Pick<AccountsGetAccountInfoParams, Field> = {}
		for (const field of fields) {
			const value = params[field]
			overrides[field] = Array.isArray(value) ? value.join(',') : value
		}
		return this.gigya.request<Account>('accounts.getAccountInfo', { ...params, ...overrides })
	}

	/**
	 * This method searches for a conflicting account: an account that uses the email associated with a social identity linked to the account currently logging in.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getConflictingAccount+REST
	 */
	public getConflictingAccount(params: BaseParams & AccountsGetConflictingAccountParams) {
		return this.gigya.request<AccountsGetConflictingAccountResponse>(
			'accounts.getConflictingAccount',
			params
		)
	}

	/**
	 * In implementations of Consent Management, this method returns all the consent statements defined for a given site.
	 *
	 * @see https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/660b99419e294030968610cbb27f42bf.html
	 */
	public getConsentsStatements(params?: BaseParams) {
		return this.gigya.request<AccountsGetConsentsStatementsResponse>(
			'accounts.getConsentsStatements'
		)
	}

	/**
	 * This method retrieves the details of a legal statement (associated with a consent statement), including the languages, versions, purpose, custom data.
	 *
	 * @see https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/660b99419e294030968610cbb27f42bf.html
	 */
	public getLegalStatements(params?: BaseParams & AccountsGetLegalStatementsParams) {
		return this.gigya.request<AccountsGetLegalStatementsResponse>(
			'accounts.getLegalStatements',
			params
		)
	}

	/**
	 * This method retrieves the counters associated with a user ID (UID).
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getCounters+REST
	 */
	public getCounters(params: BaseParams & AccountsGetCountersParams) {
		return this.gigya.request<AccountsGetCountersResponse>('accounts.getCounters', params)
	}

	/**
	 * This API allows retrieval of the public key necessary for validating an id_token returned from the accounts.getJWT API endpoint.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getJWTPublicKey+REST
	 */
	public getJWTPublicKey(params?: BaseParams & AccountsGetJWTPublicKeyParams) {
		type TParams = NonNullable<typeof params>
		type IsV2 = TParams['V2'] extends boolean ? TParams['V2'] : false
		return this.gigya.request<AccountsGetJWTPublicKeyResponse<IsV2>>(
			'accounts.getJWTPublicKey',
			params
		)
	}

	/**
	 * This API is used to obtain an OAuth2.0/OIDC compatible id_token containing an existing user's data.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getJWT+REST
	 */
	public getJWT(params: BaseParams & AccountsGetJWTParams) {
		return this.gigya.request<AccountsGetJWTResponse>('accounts.getJWT', params)
	}

	/**
	 * This method retrieves account policies.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getPolicies+REST
	 */
	public getPolicies(params: BaseParams & AccountsGetPoliciesParams) {
		return this.gigya.request<AccountsGetPoliciesResponse>('accounts.getPolicies', params)
	}

	/**
	 * This method returns the counters that were registered for the site using accounts.registerCounters.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getRegisteredCounters+REST
	 */
	public getRegisteredCounters(params?: BaseParams) {
		return this.gigya.request<AccountsGetRegisteredCountersResponse>(
			'accounts.getRegisteredCounters',
			{}
		)
	}

	/**
	 * This method retrieves the schema of the Profile object and the Data object (the site specific custom data object) in Gigya's Accounts Storage.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getSchema+REST
	 */
	public getSchema(params?: BaseParams & AccountsGetSchemaParams) {
		const p = params ?? {}
		let include = p.include
		delete p.include

		include = Array.isArray(include) ? include.join(',') : p.include

		return this.gigya.request<AccountsGetSchemaResponse>('accounts.getSchema', {
			...params,
			...(include ? { include } : {})
		})
	}

	/**
	 * This method retrieves one or more screen-sets hosted by Gigya.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.getScreenSets+REST
	 */
	public getScreenSets(params?: BaseParams & AccountsGetScreenSetsParams) {
		return this.gigya.request<AccountsGetScreenSetsResponse>('accounts.getScreenSets', params)
	}

	/**
	 * This API returns all effective consents (complete) for the specified site/group API key.
	 *
	 * @see https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/13d09b52ca21463580b418599b46de64.html
	 */
	public getSiteConsentDetails(params?: BaseParams & AccountsGetSiteConsentDetailsParams) {
		return this.gigya.request<AccountsGetSiteConsentDetailsResponse>(
			'accounts.getSiteConsentDetails',
			params
		)
	}

	/**
	 * This method retrieves Screenset's versions.
	 *
	 */
	public getScreenSetVersions(params?: BaseParams & AccountsGetScreenSetVersionsParams) {
		return this.gigya.request<AccountsGetScreenSetVersionsResponse>(
			'accounts.getScreenSetVersions',
			params
		)
	}

	/**
	 * This method imports user account data into the Accounts Storage.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.importAccount+REST
	 */
	public importFullAccount(params: BaseParams & AccountsImportFullAccountParams) {
		return this.gigya.request<AccountsImportFullAccountResponse>(
			'accounts.importFullAccount',
			params
		)
	}

	public importLiteAccount(params: BaseParams & AccountsImportLiteAccountParams) {
		return this.gigya.request<AccountsImportLiteAccountResponse>(
			'accounts.importLiteAccount',
			params
		)
	}

	/**
	 * This method imports a user's profile photo to Gigya's server.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.importProfilePhoto+REST
	 */
	public importProfilePhoto(params: BaseParams & AccountsImportProfilePhotoParams) {
		return this.gigya.request('accounts.importProfilePhoto', params)
	}

	/**
	 * When creating a custom counter, you first register it using accounts.registerCounters, then increment it using the current method.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.incrementCounters+REST
	 */
	public incrementCounters(params: BaseParams & AccountsIncrementCountersParams) {
		return this.gigya.request('accounts.incrementCounters', params)
	}

	/**
	 * This method initializes a registration process at a site.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.initRegistration+REST
	 */
	public initRegistration(params: BaseParams = {}) {
		return this.gigya.request<AccountsInitRegistrationResponse>('accounts.initRegistration', params)
	}

	/**
	 * This method checks whether a certain login identifier (username / email) is available.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.isAvailableLoginID+REST
	 */
	public isAvailableLoginID(params: BaseParams & AccountsIsAvailableLoginIDParams) {
		return this.gigya.request<AccountsIsAvailableLoginIDResponse>(
			'accounts.isAvailableLoginID',
			params
		)
	}

	/**
	 * This method merges the account identified by the provided UID with the account identified by the provided login credentials (loginID + password).
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.linkAccounts+REST
	 */
	public linkAccounts(params: BaseParams & AccountsLinkAccountsParams) {
		return this.gigya.request<Account & SessionInfo>('accounts.linkAccounts', params)
	}

	/**
	 * This method logs a user into your site and opens a session for the logged-in user on success.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.login+REST
	 */
	public login(params: BaseParams & AccountsLoginParams) {
		return this.gigya.request<Account & SessionInfo>('accounts.login', params)
	}

	/**
	 * This method logs out the specified user of your site.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.logout+REST
	 */
	public logout(params: BaseParams & AccountsLogoutParams) {
		return this.gigya.request<any>('accounts.logout', params)
	}

	/**
	 * This method notifies Gigya of an external login that happened outside of the Accounts system.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.notifyLogin+REST
	 */
	public notifyLogin(
		params: BaseParams &
			(AccountsNotifyLoginParamsSiteUID | AccountsNotifyLoginParamsProviderSessions)
	) {
		return this.gigya.request<Account & SessionInfo>('accounts.notifyLogin', params)
	}

	/**
	 * This method publishes the last imported profile photo if it hadn't been published previously.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.publishProfilePhoto+REST
	 */
	public publishProfilePhoto(params: BaseParams & AccountsPublishProfilePhotoParams) {
		return this.gigya.request('accounts.publishProfilePhoto', params)
	}

	/**
	 * When creating a custom counter, you first register it using this method, then increment it using accounts.incrementCounters.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.registerCounters+REST
	 */
	public registerCounters(params: BaseParams & AccountsRegisterCountersParams) {
		return this.gigya.request('accounts.registerCounters', params)
	}

	/**
	 * This method registers a new user at your site, in accordance with the predefined site Policies and the Schema of the Accounts Storage.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.register+REST
	 */
	public register(params: BaseParams & AccountsRegisterParams) {
		return this.gigya.request<Account & SessionInfo>('accounts.register', params)
	}

	/**
	 * This method is used to resend a validation email to unverified addresses associated with the account.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.resendVerificationCode+REST
	 */
	public resendVerificationCode(params: BaseParams & AccountsResendVerificationCodeParams) {
		return this.gigya.request('accounts.resendVerificationCode', params)
	}

	public resendDOIConfirmationEmail(params: BaseParams & AccountsResendDOIConfirmationEmail) {
		return this.gigya.request('accounts.resendDOIConfirmationEmail', params)
	}

	/**
	 * This method resets a user's password, either via email or directly.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.resetPassword+REST
	 */
	public resetPassword(params: BaseParams & AccountsResetPasswordParams) {
		return this.gigya.request<AccountsResetPasswordResponse>('accounts.resetPassword', params)
	}

	/**
	 * Searches and retrieves data from Gigya's Accounts Storage using an SQL-like query.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.search+REST
	 */
	public search(params: BaseParams & AccountsSearchParams) {
		return this.gigya.request<AccountsSearchResponse>('accounts.search', params)
	}

	/**
	 * This method sets account data into a user's account.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.setAccountInfo+REST
	 */
	public setAccountInfo(params: BaseParams & AccountsSetAccountInfoParams) {
		return this.gigya.request('accounts.setAccountInfo', params)
	}

	/**
	 * This method creates a new Consent Statement in SAP Customer Data Cloud or updates an existing one.
	 *
	 * @see https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/e598c585f0f548b799cea824ccee2bc5.html
	 */
	public setConsentsStatements(params: BaseParams & AccountsSetConsentsStatementsParams) {
		return this.gigya.request('accounts.setConsentsStatements', params)
	}

	/**
	 * This method sets the details of an existing consent statement, including the languages, versions, purpose, custom data.
	 *
	 * @see https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/6fd13c4e61c642a096323d6efdc22653.html
	 */
	public setLegalStatements(params: BaseParams & AccountsSetLegalStatementsParams) {
		return this.gigya.request('accounts.setLegalStatements', params)
	}

	/**
	 * This method is used to modify site policies regarding user registration and login.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.setPolicies+REST
	 */
	public setPolicies(params: BaseParams & AccountsSetPoliciesParams) {
		return this.gigya.request('accounts.setPolicies', params)
	}

	/**
	 * This method uploads a user's profile photo to Gigya's server.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.setProfilePhoto+REST
	 */
	public setProfilePhoto(params: BaseParams & AccountsSetProfilePhotoParams) {
		return this.gigya.request('accounts.setProfilePhoto', params)
	}

	/**
	 * This method enables you to specify a schema for Gigya's Accounts Storage.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.setSchema+REST
	 */
	public setSchema(params: BaseParams & AccountsSetSchemaParams) {
		return this.gigya.request('accounts.setSchema', params)
	}

	/**
	 * This method updates a screen-set hosted by Gigya, or creates it if it does not exist.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.setScreenSet+REST
	 */
	public setScreenSet(params: BaseParams & AccountsSetScreenSetParams) {
		return this.gigya.request('accounts.setScreenSet', params)
	}

	/**
	 * This method unregisters counters.
	 *
	 * @see http://developers.gigya.com/display/GD/accounts.unregisterCounters+REST
	 */
	public unregisterCounters(params: BaseParams & AccountsUnregisterCountersParams) {
		return this.gigya.request('accounts.unregisterCounters', params)
	}

	public getLiteToken(params: BaseParams & GetLiteTokenParams) {
		return this.gigya.request<GetLiteTokenResponse>('accounts.getLiteToken', params)
	}
}

export interface AccountsDeleteAccountParams {
	UID: string
	cid?: string
}

export interface AccountsDeleteScreenSetParams {
	screenSetID: string
}

export interface AccountsExchangeUIDSignatureParams {
	UID: string
	UIDSignature: string
	signatureTimestamp: string
	userKey: string
}

export interface AccountsExchangeUIDSignatureResponse {
	UID: string
	UIDSignature: string
	signatureTimestamp: string
}

export interface AccountsFinalizeRegistrationParams {
	regToken: string
	include?: string
	allowAccountsLinking?: boolean
	targetEnv: TargetEnv
}

export const AccountInfoIncludes = [
	'identities-active',
	'identities-all',
	'identities-global',
	'loginIDs',
	'emails',
	'profile',
	'data',
	'password',
	'isLockedOut',
	'lastLoginLocation',
	'regSource',
	'irank',
	'rba',
	'subscriptions',
	'userInfo',
	'preferences',
	'memberships',
	'communications',
	'groups'
] as const

export type AccountInfoInclude = (typeof AccountInfoIncludes)[number]

export const AccountInfoExtraProfileFields = [
	'languages',
	'address',
	'phones',
	'education',
	'educationLevel',
	'honors',
	'publications',
	'patents',
	'certifications',
	'likes',
	'professionalHeadline',
	'bio',
	'industry',
	'specialities',
	'work',
	'skills',
	'religion',
	'politicalView',
	'interestedIn',
	'relationshipStatus',
	'hometown',
	'favorites',
	'followersCount',
	'followingCount',
	'username',
	'name',
	'locale',
	'verified',
	'timezone',
	'samlData'
] as const

export type AccountInfoExtraProfileFields = (typeof AccountInfoExtraProfileFields)[number]

export const AccountInfoCommunicationsInclude = ['all'] as const

export type AccountInfoCommunicationsInclude = (typeof AccountInfoCommunicationsInclude)[number]

export type AutoComplete<T> = T | (string & {})
export type StringList<T> = Array<AutoComplete<T>> | string

type FindByAttr<T extends string> = { [k in T]: string }
const findByKeys = ['_email', '_uid', '_username', '_phoneNumber'] as const
type FindByKey = (typeof findByKeys)[number]

export interface AccountsGetAccountInfoParams {
	UID?: string
	regToken?: string
	include?: StringList<AccountInfoInclude>
	extraProfileFields?: StringList<AccountInfoExtraProfileFields>
	includeCommunications?: StringList<AccountInfoCommunicationsInclude>
	findBy?: Partial<FindByAttr<FindByKey>>
}

export interface AccountsGetConflictingAccountParams {
	regToken: string
}

export interface AccountsGetConflictingAccountResponse {
	conflictingAccount: {
		loginProviders: Array<string>
	}
	loginID: string
}

export interface AccountsGetConsentsStatementsResponse {
	preferences: any
}

export interface AccountsGetLegalStatementsParams {
	lang?: any
	consentId?: string
	format?: string | null
	callback?: string
	httpStatusCodes?: boolean
}

export interface AccountsGetLegalStatementsResponse {
	legalStatements: any
}

export interface AccountsGetCountersParams {
	UID?: string
	regToken?: string
	counters: Array<Counter>
}

export interface AccountsGetCountersResponse {
	counters: Array<Counter>
}

export interface AccountsGetJWTPublicKeyParams {
	V2?: boolean
}

type KeyDetails = {
	kty: string
	alg: string
	use: string
	kid: string
	n: string
	e: string
}

//interface AccountsGetJWTPublicKeyResponse {}
export type AccountsGetJWTPublicKeyResponse<V2 extends boolean> = V2 extends true
	? Array<KeyDetails>
	: KeyDetails

export interface AccountsGetJWTParams {
	targetUID: string
	fields?: string
	expiration?: number
}

export interface AccountsGetJWTResponse {
	id_token: string
	ignoredFields: string
}

export interface AccountsGetPoliciesParams {
	sections?: string
	filter?: 'full' | 'explicitOnly'
}

export interface AccountsGetPoliciesResponse {
	accountOptions: {
		verifyEmail: boolean
		verifyProviderEmail: boolean
		allowUnverifiedLogin: boolean
		loginIdentifiers: string
		defaultLanguage: string
		loginIdentifierConflict:
			| 'ignore'
			| 'failOnSiteConflictingIdentity'
			| 'failOnAnyConflictingIdentity'
		preventLoginIDHarvesting: boolean
		sendAccountDeletedEmail: boolean
		sendWelcomeEmail: boolean
		welcomeEmailTemplates: { [key: string]: string }
	}
	emailNotifications: {
		confirmationEmailTemplates: { [key: string]: string }
		accountDeletedEmailTemplates: { [key: string]: string }
		accountDeletedEmailDefaultLanguage: string
		confirmationEmailDefaultLanguage: string
	}
	emailVerification: {
		autoLogin: boolean
		nextURL: string
		nextURLMapping: { [key: string]: string }
		verificationEmailExpiration: number
		defaultLanguage: string
		emailTemplates: { [key: string]: string }
	}
	gigyaPlugins: {
		defaultRegScreenSet: string
		defaultMobileRegScreenSet: string
		sessionExpiration: SessionExpiration
		rememberSessionExpiration: SessionExpiration
	}
	passwordComplexity: {
		minCharGroups: number
		minLength: number
		regExp: string
	}
	passwordReset: {
		requireSecurityCheck: boolean
		securityFields: Array<Array<string>>
		resetURL: string
		tokenExpiration: number
		defaultLanguage: string
		emailTemplates: { [key: string]: string }
		sendConfirmationEmail: boolean
	}
	profilePhoto: {
		thumbnailWidth: number
		thumbnailHeight: number
	}
	registration: {
		requireCaptcha: boolean
		requireSecurityQuestion: boolean
		requireLoginID: boolean
		enforceCoppa: boolean
	}
	security: {
		accountLockout: {
			failedLoginThreshold: number
			lockoutTimeSec: number
			failedLoginResetSec: number
		}
		captcha: {
			failedLoginThreshold: number
		}
		ipLockout: {
			hourlyFailedLoginThreshold: number
			lockoutTimeSec: number
		}
		passwordChangeInterval: number
		passwordHistorySize: number
	}
	twoFactorAuth: {
		providers: Array<{
			name: string
			enabled: boolean
			params: { [key: string]: string }
		}>
	}
	federation: {
		allowMultipleIdentities: boolean
	}
	doubleOptIn: {
		nextURL: string
		defaultLanguage: string
		confirmationLinkExpiration: number
		confirmationEmailTemplates: { [key: string]: string }
	}
	authentication: {
		methods: AuthMethods
	}
}

export interface AccountsSetPoliciesParams {
	accountOptions?: {
		verifyEmail?: boolean
		verifyProviderEmail?: boolean
		allowUnverifiedLogin?: boolean
		loginIdentifiers?: string
		defaultLanguage?: string
		loginIdentifierConflict?:
			| 'ignore'
			| 'failOnSiteConflictingIdentity'
			| 'failOnAnyConflictingIdentity'
		preventLoginIDHarvesting?: boolean
		sendAccountDeletedEmail?: boolean
		sendWelcomeEmail?: boolean
		welcomeEmailTemplates?: { [key: string]: string }
	} | null
	emailNotifications?: {
		confirmationEmailTemplates?: { [key: string]: string }
		accountDeletedEmailTemplates?: { [key: string]: string }
		accountDeletedEmailDefaultLanguage?: string
		confirmationEmailDefaultLanguage?: string
	} | null
	emailVerification?: {
		autoLogin?: boolean
		nextURL?: string
		nextURLMapping?: { [key: string]: string }
		verificationEmailExpiration?: number
		defaultLanguage?: string
		emailTemplates?: { [key: string]: string }
	} | null
	gigyaPlugins?: {
		defaultRegScreenSet?: string
		defaultMobileRegScreenSet?: string
		sessionExpiration?: SessionExpiration
		rememberSessionExpiration?: SessionExpiration
	} | null
	passwordComplexity?: {
		minCharGroups?: number
		minLength?: number
		regExp?: string
	} | null
	passwordReset?: {
		requireSecurityCheck?: boolean
		securityFields?: Array<Array<string>>
		resetURL?: string
		tokenExpiration?: number
		defaultLanguage?: string
		emailTemplates?: { [key: string]: string }
		sendConfirmationEmail?: boolean
	} | null
	profilePhoto?: {
		thumbnailWidth?: number
		thumbnailHeight?: number
	} | null
	registration?: {
		requireCaptcha?: boolean
		requireSecurityQuestion?: boolean
		requireLoginID?: boolean
		enforceCoppa?: boolean
	} | null
	security?: {
		accountLockout?: {
			failedLoginThreshold?: number
			lockoutTimeSec?: number
			failedLoginResetSec?: number
		}
		captcha?: {
			failedLoginThreshold?: number
		}
		ipLockout?: {
			hourlyFailedLoginThreshold?: number
			lockoutTimeSec?: number
		}
		passwordChangeInterval?: number
		passwordHistorySize?: number
	} | null
	twoFactorAuth?: {
		providers?: Array<{
			name?: string
			enabled?: boolean
			params?: { [key: string]: string }
		}>
	} | null
	federation?: {
		allowMultipleIdentities?: boolean
	} | null
	doubleOptIn?: {
		nextURL?: string
		defaultLanguage?: string
		confirmationLinkExpiration?: number
		confirmationEmailTemplates?: {
			[key: string]: string
		}
	} | null
	authentication?: {
		methods?: AuthMethods
	}
}

export interface AccountsGetRegisteredCountersResponse {
	counters: Array<Counter>
}

export interface AccountsSetAccountInfoParams<DataType = any> {
	UID?: string
	regToken?: string
	addLoginEmails?: string
	conflictHandling?: 'fail' | 'saveProfileAndFail'
	data?: DataType
	subscriptions?: Subscriptions
	lang?: string
	communications?: Communications<'write'>
	preferences?: Preferences<'write'>
	isActive?: boolean
	isLockedOut?: boolean
	isVerified?: boolean
	newPassword?: string
	password?: string
	phoneNumber?: string
	profile?: Profile
	removeLoginEmails?: string
	requirePasswordChange?: boolean
	secretAnswer?: string
	secretQuestion?: string
	securityOverride?: boolean
	rba?: {
		riskPolicy: string
		riskPolicyLocked: boolean
	}
	username?: string
	created?: string
	regSource?: string
	muteWebhooks?: boolean
}

export interface AccountsSetConsentsStatementsParams {
	preferences: any
	format?: string | null
	callback?: string
	httpStatusCodes?: boolean
}

export interface AccountsSetLegalStatementsParams {
	lang?: any
	consentId?: string
	legalStatements?: any
	format?: string | null
	callback?: string
	httpStatusCodes?: boolean
}

export interface AccountsImportFullAccountParams {
	UID: string
	importPolicy?: string
	// ISO 8601 date
	created?: string
	data?: any
	dataCenter?: DataCenter
	emails?: {
		verified?: string[]
		unverified?: string[]
	}
	identities?: Identity[]
	isActive?: boolean
	isRegistered?: boolean
	isVerified?: boolean
	// ISO 8601 date
	lastLogin?: string
	// ISO 8601 date
	lastUpdated?: string
	loginIds?: {
		username?: string
		emails?: string[]
		unverifiedEmails?: string[]
	}
	password?: {
		compoundHashedPassword?: string
		hashedPassword?: string
		hashSettings?: {
			algorithm: string
			salt: string
			rounds: number
			format: string
			binaryFormat: string
			url: string
		}
		secretQuestionAndAnswer?: {
			secretQuestion: string
			secretAnswer: string
		}
	}
	preferences?: Preferences<'read'>
	profile?: Profile
	// ISO 8601 date
	registered?: string
	// reg url
	regSource?: string
	subscriptions?: Subscriptions
	// ISO 8601 date
	verified?: string
	// language code for consent
	lang?: string
	providerSessions?: any
}

export interface AccountsImportFullAccountResponse {
	identityConflicts: {
		provider: string
		providerUID: string
	}[]
}

export interface AccountsImportLiteAccountParams {
	email: string
	profile: Profile
	preferences: Preferences<'write'>
	subscriptions: Subscriptions
	data: Record<string, unknown>
}

export interface AccountsImportLiteAccountResponse {}

export interface AccountsImportProfilePhotoParams {
	url: string
	UID?: string
	regToken?: string
	publish?: boolean
}

export interface AccountsIncrementCountersParams {
	UID?: string
	regToken?: string
	counters: Array<Counter>
}

export interface AccountsInitRegistrationResponse {
	regToken: string
}

export interface AccountsLinkAccountsParams {
	UID?: string
	regToken?: string
	loginID: string
	password: string
	include?: string
	targetEnv: TargetEnv
}

export interface AccountsPublishProfilePhotoParams {
	UID?: string
	regToken?: string
	thumbnail?: string
}

export interface AccountsRegisterCountersParams {
	counters: Array<Counter>
}

export interface AccountsRegisterParams {
	username?: string
	email?: string
	password: string
	regToken?: string
	profile?: Profile
	captchaText?: string
	captchaToken?: string
	cid?: string
	data?: any
	subscriptions?: any
	preferences?: any
	finalizeRegistration?: boolean
	secretQuestion?: string
	secretAnswer?: string
	lang?: string
	targetEnv?: TargetEnv
	include?: string
	sessionExpiration?: SessionExpiration
	siteUID?: string
	regSource?: string
}

export interface AccountsResendVerificationCodeParams {
	UID?: string
	regToken?: string
	email?: string
}

export interface AccountsResendDOIConfirmationEmail {
	email: string
	subscriptionId: string
	lang?: string
	context?: any
}

export interface AccountsResetPasswordParams {
	loginID: string
	passwordResetToken: string
	newPassword: string
	secretAnswer: string
	securityFields: string
	email?: string
	lang?: string
	sendEmail?: string
}

export interface AccountsResetPasswordResponse {
	secretQuestion?: string
	passwordResetToken?: string
	emails?: {
		verified: Array<string>
		unverified: Array<string>
	}
	UID?: string
}

export interface AccountsSearchParams {
	query?: string
	querySig?: string
	expTime?: number
	openCursor?: boolean
	cursorId?: string
	timeout?: number
	restrictedQuery?: string
}

export interface AccountsSearchResponse {
	results: Array<Account>
	nextCursorId?: string
	objectsCount: number
	totalCount: number
}

export interface AccountsSetProfilePhotoParams {
	UID?: string
	regToken?: string
	photoBytes?: string
	publish?: boolean
}

export interface AccountsLoginParams {
	loginID: string
	password: string
	captchaToken?: string
	captchaText?: string
	actionAttributes?: { [key: string]: string }
	cid?: string
	include?: Array<string> | string
	loginMode?: 'standard' | 'link' | 'reAuth'
	sessionExpiration?: SessionExpiration
	targetEnv?: TargetEnv
}

export interface AccountsLogoutParams {
	UID: string
	cid?: string
}

export interface AccountsNotifyLoginParams {
	siteUID?: string
	providerSessions?: { [key: string]: any }
	cid?: string
	targetEnv?: TargetEnv
	regSource?: string
	sessionExpiration?: SessionExpiration
}

export interface AccountsNotifyLoginParamsSiteUID extends AccountsNotifyLoginParams {
	siteUID: string
}

export interface AccountsNotifyLoginParamsProviderSessions extends AccountsNotifyLoginParams {
	providerSessions: { [key: string]: any }
}

export type AccountsSchemaWriteAccess = 'serverOnly' | 'clientCreate' | 'clientModify'
export type AccountsSchemaType =
	| 'integer'
	| 'long'
	| 'float'
	| 'basic-string'
	| 'string'
	| 'text'
	| 'date'
	| 'boolean'
export type AccountsSchemaEncrypt = 'AES' | ''

export interface AccountsSetSchemaParams {
	profileSchema?: {
		fields: {
			[key: string]: AccountsProfileSetSchemaField
		}
	}
	dataSchema?: {
		fields: {
			[key: string]: AccountsDataSetSchemaField
		}
		dynamicSchema?: boolean
	}
	subscriptionsSchema?: {
		fields: {
			[key: string]: AccountsSubscriptionSetSchemaField
		}
	}
	preferencesSchema?: {
		fields: {
			[key: string]: AccountsPreferencesSetSchemaField
		}
	}
	scope?: 'group' | 'site'
}

export interface AccountsProfileSetSchemaField {
	required?: boolean
	writeAccess?: AccountsSchemaWriteAccess
	languages?: Array<string>
}

export interface AccountsDataSetSchemaField extends AccountsProfileSetSchemaField {
	allowNull?: boolean
	encrypt?: AccountsSchemaEncrypt | null
	format?: string | null
	type?: AccountsSchemaType
}

export interface AccountsSubscriptionSetSchemaField extends AccountsProfileSetSchemaField {
	doubleOptIn?: boolean
	description?: string | null
	type?: AccountsSchemaType
}

export interface AccountsPreferencesSetSchemaField extends AccountsProfileSetSchemaField {
	type?: 'consent'
	currentDocDate?: string
	minDocDate?: string
	currentDocVersion?: number
	minDocVersion?: number
	format?: string | null
}

export const SchemaIncludes = [
	'profileSchema',
	'dataSchema',
	'subscriptionsSchema',
	'preferencesSchema',
	'internalSchema'
] as const
export type SchemaIncludes = (typeof SchemaIncludes)[number]

export interface AccountsGetSchemaParams {
	filter?: 'full' | 'explicitOnly' | 'clientOnly'
	scope?: 'effective' | 'group' | 'site'
	include?: StringList<SchemaIncludes>
	internalSchema?: boolean
}

export interface AccountsGetSchemaResponse {
	profileSchema: {
		fields: {
			[key: string]: AccountsGetSchemaField
		}
	}
	dataSchema: {
		fields: {
			[key: string]: AccountsGetSchemaField
		}
	}
	subscriptionsSchema: {
		fields: {
			[key: string]: AccountsGetSchemaField
		}
	}
	preferencesSchema: {
		fields: {
			[key: string]: AccountsPreferencesGetSchemaField
		}
	}
}

export interface AccountsGetSchemaField {
	type: AccountsSchemaType
	required: boolean
	writeAccess: AccountsSchemaWriteAccess
	allowNull: boolean
	languages?: Array<string>
	encrypt?: AccountsSchemaEncrypt
	format?: string
}

export interface AccountsPreferencesGetSchemaField {
	type: 'consent'
	required: boolean
	writeAccess: AccountsSchemaWriteAccess
	format?: string
	currentDocDate?: string
	minDocDate?: string
	currentDocVersion?: number
	minDocVersion?: number
	customData?: Record<string, string>
	consentVaultRetentionPeriod?: number
	legalStatements: Record<
		string,
		{
			purpose: string
			documentUrl: string
			legalStatementStatus: AutoComplete<'Published'>
		}
	>
}

export interface ScreenSet {
	screenSetID: string
	html: string
	css: string
	javascript?: string
	translations?: {
		[languageCode: string]: {
			[translationKey: string]: string
		}
	}
	metadata: {
		designerHtml?: string // Set without designer HTML to disable UI Builder.
		desc?: string
		targetEnv?: string
		lastModified?: string
		version?: number
	}
}

export interface AccountsGetScreenSetsParams {
	screenSetIDs?: string | Array<string>
	include?: string
	version?: number
}

export interface AccountsGetScreenSetsResponse {
	screenSets: Array<ScreenSet>
}

export interface AccountsGetSiteConsentDetailsParams {
	apiKey?: string
}

export interface AccountsGetSiteConsentDetailsResponse {
	siteConsentDetails: any
}

export interface ScreenSetVersion {
	version: number
	lastModified: number
	comment: string
	uiBuilderSupport: boolean
}

export interface AccountsGetScreenSetVersionsParams {
	screenSetID: string
	startVersion?: number
	count?: number
}

export interface AccountsGetScreenSetVersionsResponse {
	screenSetVersions: ScreenSetVersion[]
}

export interface AccountsIsAvailableLoginIDParams {
	loginID: string
}

export interface AccountsIsAvailableLoginIDResponse {
	isAvailable: boolean
}

export interface AccountsSetScreenSetParams {
	screenSetID: string
	comment: string
	html: string
	css: string
	javascript?: string
	translations?: {
		[languageCode: string]: {
			[translationKey: string]: string
		}
	}
	metadata?: {
		designerHtml?: string // Set without designer HTML to disable UI Builder.
		desc?: string
		targetEnv?: string
		lastModified?: string
	}
}

export interface AccountsUnregisterCountersParams {
	counters: Array<Counter>
}

export interface AuthMethods {
	password: { enabled: boolean }
	push?: { enabled: boolean }
}

export type GetLiteTokenParams =
	| {
			emailToken: string
	  }
	| {
			email: string
	  }

export interface GetLiteTokenResponse {
	token: string
}

export default Accounts
